import { IExternalConsultationHistoryGridModel } from "@/Administration/Models/UI/ExternalConsultationHistory/IExternalConsultationHistoryGridModel";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { CheckedListControl } from "@/Common/Components/Inputs/CheckedListControl";
import { MultiDateSelector } from "@/Common/Components/Inputs/MultiDateSelector";
import { FileType } from "@/Common/Models/FileType";
import { CheckboxUIModelT } from "@/Common/Models/UI/Inputs/CheckboxUIModel";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { XComEventDirection } from "./Enums/XComEventDirection";
import { XComEventTypes } from "./Enums/XComEventTypes";
import { XComOptions } from "./Enums/XComOptions";
import { XComLogsHelper } from "./XComLogsHelper";

interface IXComLogsSearchSettingProps {
    model: IExternalConsultationHistoryGridModel;
    id?: string;
    className?: string;
    onCancelCallBack: () => void;
}

class XComLogsSearchSettingState {
    public SearchText: string;
    public DateFrom: Date;
    public DateTo: Date;
    public EventTypes: CheckboxUIModelT<XComEventTypes>[];
    public EventTypesAllChecked: boolean;
    public FileTypes: CheckboxUIModelT<FileType>[];
    public FileTypesAllChecked: boolean;
    public EventDirection: CheckboxUIModelT<XComEventDirection>[];
    public EventDirectionAllChecked: boolean;
    public Options: CheckboxUIModelT<XComOptions>[];
    public OptionsAllChecked: boolean;
    public PageSize: number;

    public static getDefault(props: IXComLogsSearchSettingProps): XComLogsSearchSettingState {
        const s = new XComLogsSearchSettingState();
        s.SearchText = props.model.get("SearchText");
        s.DateFrom = props.model.get("DateFrom");
        s.DateTo = props.model.get("DateTo");
        s.PageSize = props.model.get("PageSize") ?? 100;

        s.EventTypes = Object.keys(XComEventTypes)
            .filter(p => typeof XComEventTypes[p] === "number")
            .map(p =>
                new CheckboxUIModelT<XComEventTypes>(
                    {
                        Id: p,
                        Name: XComLogsHelper.getEventTypeName(p),
                        Model: XComEventTypes[p],
                        Checked: props.model.get("EventTypes")?.toArray().some(f => f == XComEventTypes[p])
                    } as CheckboxUIModelT<XComEventTypes>
                ));
        s.EventTypesAllChecked = s.EventTypes.all(p => p.Checked);

        const includedFileTypes = ["CONSULTATIONREQUEST", "EXAMREQUEST", "ENCOUNTER", "MEDICALSUMMARY", "OBSTETRICALFILE", "RADPDF", "WORKERCLAIM", "WorkSafeBcForm", "DOCUMENT", "LABPDF", "MEDICATIONSUMMARY", "PatientGrowthChart", "Invoice"];
        s.FileTypes = includedFileTypes
            .filter(p => typeof FileType[p] === "number")
            .map(p =>
                new CheckboxUIModelT<FileType>(
                    {
                        Id: p,
                        Name: XComLogsHelper.getFileTypeName(p),
                        Model: FileType[p],
                        Checked: props.model.get("FileTypes")?.toArray().some(f => f == FileType[p])
                    } as CheckboxUIModelT<FileType>
                ));
        s.FileTypesAllChecked = s.FileTypes.all(p => p.Checked);

        s.EventDirection = Object.keys(XComEventDirection)
            .filter(p => typeof XComEventDirection[p] === "number")
            .map(p =>
                new CheckboxUIModelT<XComEventDirection>(
                    {
                        Id: p,
                        Name: XComLogsHelper.getEventDirectionName(p),
                        Model: XComEventDirection[p],
                        Checked: props.model.get("EventDirections")?.toArray().some(f => f == XComEventDirection[p])
                    } as CheckboxUIModelT<XComEventDirection>
                ));

        s.Options = Object.keys(XComOptions)
            .filter(p => typeof XComOptions[p] === "number")
            .map(p =>
                new CheckboxUIModelT<XComOptions>(
                    {
                        Id: p,
                        Name: XComLogsHelper.getOptionName(p),
                        Model: XComOptions[p],
                        Checked: props.model.get("Options")?.toArray().some(f => f == XComOptions[p])
                    } as CheckboxUIModelT<XComOptions>
                ));

        return s;
    }
}

class XComLogsSearchSetting extends BaseReactComponent<IXComLogsSearchSettingProps, XComLogsSearchSettingState> {
    constructor(props: IXComLogsSearchSettingProps) {
        super(props);
        this.state = XComLogsSearchSettingState.getDefault(this.props);
    }

    public componentDidMount(): void {
        super.setOverlay("ExternalConsultationHistorySearchSettings", { transparent: true });
    }

    public render(): JSX.Element {
        return (
            <div id={this.props.id} className={this.props.className} style={{ width: "400px" }}>
                <table className="f-content">
                    <thead>
                        <tr>
                            <th>
                                <label style={{ display: "inline" }}>{cacheStore.GetString("ConsultationDateLabel")}</label>
                            </th>
                            <th>
                                <CheckboxControl
                                    id="event_check"
                                    label={cacheStore.GetString("EventsLabel")}
                                    checked={this.state.EventTypesAllChecked}
                                    onChange={checked => this.mutateState(s => {
                                        s.EventTypes = s.EventTypes.mutate(p => p.Checked = checked);
                                        s.EventTypesAllChecked = s.EventTypes.all(p => p.Checked);
                                    })}
                                />
                            </th>
                            <th>
                                <CheckboxControl
                                    id="type_check"
                                    label={cacheStore.GetString("FileTypeLabel")}
                                    checked={this.state.FileTypesAllChecked}
                                    onChange={checked => this.mutateState(s => {
                                        s.FileTypes = s.FileTypes.mutate(p => p.Checked = checked);
                                        s.FileTypesAllChecked = s.FileTypes.all(p => p.Checked);
                                    })}
                                />
                            </th>
                            <th>
                                <CheckboxControl
                                    id="direction_check"
                                    label={cacheStore.GetString("EventDirectionLabel")}
                                    checked={this.state.EventDirectionAllChecked}
                                    onChange={checked => this.mutateState(s => {
                                        s.EventDirection = s.EventDirection.mutate(p => p.Checked = checked);
                                        s.EventDirectionAllChecked = s.EventDirection.all(p => p.Checked);
                                    })}
                                />
                            </th>
                            <th>
                                <CheckboxControl
                                    id="options_check"
                                    label={cacheStore.GetString("OptionsLabel")}
                                    checked={this.state.OptionsAllChecked}
                                    onChange={checked => this.mutateState(s => {
                                        s.Options = s.Options.mutate(p => p.Checked = checked);
                                        s.OptionsAllChecked = s.Options.all(p => p.Checked);
                                    })}
                                />
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>
                                <MultiDateSelector
                                    id="external_search_date_selector"
                                    fromChange={date => this.mutateState(p => p.DateFrom = date)}
                                    toChange={date => this.mutateState(p => p.DateTo = date)}
                                    fromSetTodayLabel={cacheStore.GetString("TodayLabel")}
                                    toSetTodayLabel={cacheStore.GetString("TodayLabel")}
                                    from={this.state.DateFrom}
                                    to={this.state.DateTo}
                                />
                            </td>
                            <td>
                                <CheckedListControl
                                    DataSource={this.state.EventTypes.toImmutable()}
                                    onChange={(isChecked, id) => this.mutateState(s => s.EventTypes.mutate(p => p.Checked = isChecked, p => p.Id == id))}
                                />
                            </td>
                            <td>
                                <CheckedListControl
                                    DataSource={this.state.FileTypes.toImmutable()}
                                    onChange={(isChecked, id) => this.mutateState(s => s.FileTypes.mutate(p => p.Checked = isChecked, p => p.Id == id))}
                                />
                            </td>
                            <td>
                                <CheckedListControl
                                    DataSource={this.state.EventDirection.toImmutable()}
                                    onChange={(isChecked, id) => this.mutateState(s => s.EventDirection.mutate(p => p.Checked = isChecked, p => p.Id == id))}
                                />
                            </td>
                            <td>
                                <CheckedListControl
                                    DataSource={this.state.Options.toImmutable()}
                                    onChange={(isChecked, id) => this.mutateState(s => s.Options.mutate(p => p.Checked = isChecked, p => p.Id == id))}
                                />
                            </td>
                        </tr>
                    </tbody>
                </table>
                <div className="f-footer clearfix">
                    <div className="flex-container flex-end flex-gap-xs">
                        <ButtonControl
                            className="btn btn-default"
                            onClick={this.onCancel.bind(this)}
                            label={cacheStore.GetString("CancelLabel")}
                        />
                        <ButtonControl
                            variant="brand"
                            onClick={this.onApply.bind(this)}
                            label={cacheStore.GetString("Ok_Label")}
                        />
                    </div>
                </div>
            </div>
        );
    }

    private onCancel(): void {
        this.props.onCancelCallBack();
    }
    private onApply(): void {
        sessionStore.dispatch(LocalOperations.AdministrationXComLogs.SearchSettingsApplied, { settings: this.state });
        this.props.onCancelCallBack();
    }
}

export { XComLogsSearchSetting, XComLogsSearchSettingState };
